import { startTransition } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import '@fontsource-variable/inter/wght.css';
import '@fontsource-variable/space-grotesk/wght.css';
/*
 * The stylesheets every public page needs, in the order the app has always
 * loaded them. The app's own sheets (the studio, the shell, each screen) are
 * imported by `App.tsx` and arrive with the app, so the landing page does not
 * wait for half a megabyte of CSS it never uses before it can paint.
 */
import './tailwind.css';
import './styles.css';
import './landing.css';
import './blog.css';
import { PageTree } from './PageTree.js';
import { analytics } from './analytics/analytics.js';
import { loadCatalogue, readStoredLocale } from './i18n/index.js';
import { resolvePrivacyRegion } from './consent/region.js';

const container = document.getElementById('root');
if (!container) throw new Error('#root missing');

/*
 * `#root` arrives holding the landing page the build rendered. The inline
 * script in `index.html` marks every visit it is not right for (another
 * address, the app host, a visitor reading in another language) and hides it;
 * there it is cleared and React draws the page from nothing, as it always has.
 * In a dev server there is no prerendered markup at all.
 */
const html = document.documentElement;
if (html.hasAttribute('data-spa')) {
  container.replaceChildren();
  html.removeAttribute('data-spa');
}
const prerendered = container.hasChildNodes();

// Asks which privacy model applies (one small request; strict until it answers),
// then arms the analytics gate. Nothing is downloaded or stored until analytics
// is allowed, and nothing at all happens off the real hosts.
resolvePrivacyRegion();
analytics.start();

if (prerendered) {
  /*
   * A transition, so React adopts the page in small slices between frames
   * instead of one long task that leaves a phone unable to answer a tap for
   * the second it takes. The page is already on screen, and its links work,
   * while it happens.
   */
  startTransition(() => {
    hydrateRoot(container, <PageTree prerendered />);
  });
} else {
  const draw = () => createRoot(container).render(<PageTree prerendered={false} />);
  // A visitor who reads in Turkish gets the Turkish strings before the first
  // frame, so nothing is drawn in English first. If they cannot be fetched the
  // page is drawn anyway, in English, rather than not at all. Either way the
  // screen waits for the full stylesheet, so it never paints half-styled.
  const strings = loadCatalogue(readStoredLocale()).catch(() => undefined);
  void Promise.all([strings, stylesheetsApplied()]).then(draw);
}

/**
 * Resolves once every stylesheet in the page has been applied.
 *
 * The built `index.html` carries only the landing page's own rules inline and
 * fetches the full stylesheet without blocking the first paint, as a preload
 * that turns itself into a stylesheet when it lands (scripts/build.mjs). Every
 * other screen is drawn from that full sheet, so it waits for it here. The
 * preload is turned into a stylesheet from this side as well, in case its own
 * handler was refused; doing it twice is harmless. A sheet that fails to load
 * does not hold the screen back: it is drawn regardless, as it was before.
 */
function stylesheetsApplied(): Promise<void> {
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel="preload"][as="style"]')) {
    link.rel = 'stylesheet';
  }
  const waiting = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
    (link) =>
      new Promise<void>((resolve) => {
        if (link.sheet) {
          resolve();
          return;
        }
        link.addEventListener('load', () => resolve(), { once: true });
        link.addEventListener('error', () => resolve(), { once: true });
      }),
  );
  return Promise.all(waiting).then(() => undefined);
}
